import { useCallback, useEffect, useMemo, useState } from "react"
import { ModalBody } from "@mezo-org/mezo-clay"
import { useQueryClient } from "@tanstack/react-query"
import { zeroAddress } from "viem"
import { useAccount } from "wagmi"

import { RootState, selectTokenDecimals, useDappSelector } from "#/redux-state"
import ApproveAndCallProgressSteps, {
  ApproveAndCallProgressStepsProvider,
  ApproveAndCallProgressStepsSubmitButton,
} from "#/shared/components/ApproveAndCallProgressSteps"
import FailedActionModalBody from "#/shared/components/modals/FailedActionModalBody"
import WaitingForActionModalBody from "#/shared/components/modals/WaitingForActionModalBody"
import SummaryList from "#/shared/components/SummaryList"
import TermsOfUseCheckbox from "#/shared/components/TermsOfUseCheckbox"
import { IS_MEZO_MAINNET, MEZO_CHAIN, vault } from "#/shared/constants"
import {
  useDepositIntoVault,
  usePreviewShares,
  useVaultStats,
  useVaultUnderlyingToken,
} from "#/shared/hooks"
import useVaultPosition from "#/shared/hooks/vaults/useVaultPosition"
import { VaultDepositFormValue } from "#/shared/types"
import {
  bigIntToHumanReadableFormat,
  getCurrencySymbol,
  vaultUtils,
} from "#/shared/utils"
import { SwitchChainButton } from "#/ui/Button"
import {
  ComponentStepProps,
  StepperAbsoluteModalFooter,
  useStepperModalContext,
} from "#/ui/Modal"

export default function VaultReviewStep({
  onClickContinue,
}: ComponentStepProps) {
  const { address } = useAccount()
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)

  const {
    values: { token, amount },
  } = useStepperModalContext<VaultDepositFormValue>()

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, token),
  )

  const vaultAddress = vault.VAULT_DEPOSIT_TOKEN_TO_VAULT_ADDRESS[token]

  const {
    depositIntoVault,
    isWaitingForTxReceipt,
    isPending,
    isError,
    isTxConfirmed,
  } = useDepositIntoVault(vaultAddress)

  const deposit = useCallback(async () => {
    await depositIntoVault({ amount, receiver: address })
  }, [amount, address, depositIntoVault])

  const { data: underlyingTokenAddress } = useVaultUnderlyingToken(vaultAddress)

  const { queryKey: vaultPositionQueryKey } = useVaultPosition(
    vaultAddress,
    address,
  )

  const { queryKey: vaultStatsQueryKey } = useVaultStats(vaultAddress)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!isTxConfirmed) return

    const onContinue = async () => {
      await queryClient.invalidateQueries({
        queryKey: vaultStatsQueryKey,
      })
      await queryClient.invalidateQueries({
        queryKey: vaultPositionQueryKey,
      })
      onClickContinue()
    }

    onContinue()
  }, [
    isTxConfirmed,
    onClickContinue,
    queryClient,
    vaultPositionQueryKey,
    vaultStatsQueryKey,
  ])

  const { data: shares, isFetching: isFetchingShares } = usePreviewShares(
    vault.VAULT_DEPOSIT_TOKEN_TO_VAULT_ADDRESS[token],
    amount,
  )

  const summary = useMemo(
    () => [
      {
        label: "Deposit",
        endEnhancer: `${bigIntToHumanReadableFormat(
          amount ?? 0n,
          tokenDecimals,
        )} ${getCurrencySymbol(token)}`,
      },
      {
        label: "Receive",
        endEnhancer: `${bigIntToHumanReadableFormat(shares ?? 0n)} ${
          vault.VAULT_DEPOSIT_TOKEN_TO_SHARE_TOKEN_NAME[token]
        }`,
        isFetching: isFetchingShares,
      },
      {
        label: "Avg APY",
        endEnhancer: `${vaultUtils.getAvgAPY()}%`,
      },
    ],
    [amount, shares, token, tokenDecimals, isFetchingShares],
  )

  if (isPending || isWaitingForTxReceipt || isTxConfirmed)
    return <WaitingForActionModalBody title="Waiting for transaction..." />

  if (isError)
    return (
      <FailedActionModalBody
        title="Something went wrong"
        description="Please try again."
        nextStepButtonLabel="Try again"
        onClickNextStep={deposit}
      />
    )

  return (
    <ApproveAndCallProgressStepsProvider
      token={underlyingTokenAddress ?? zeroAddress}
      amountToApprove={amount ?? 0n}
      callTransactionCallback={deposit}
      spender={vaultAddress}
      chainId={MEZO_CHAIN.id}
    >
      <ModalBody>
        <SummaryList list={summary} />
        <ApproveAndCallProgressSteps
          token={token}
          callTransactionStepTitle="Deposit"
        />
      </ModalBody>
      <StepperAbsoluteModalFooter>
        <TermsOfUseCheckbox
          checked={hasAcceptedTerms}
          onChange={(ev) => setHasAcceptedTerms(ev.target.checked)}
        />
        <SwitchChainButton
          width="100%"
          size="large"
          shape="default"
          label={IS_MEZO_MAINNET ? "Mezo" : "Mezo Testnet"}
          chainId={MEZO_CHAIN.id}
          disabled={!hasAcceptedTerms}
        >
          <ApproveAndCallProgressStepsSubmitButton
            size="large"
            width="100%"
            disabled={!hasAcceptedTerms}
            isLoading={isWaitingForTxReceipt}
          >
            Confirm
          </ApproveAndCallProgressStepsSubmitButton>
        </SwitchChainButton>
      </StepperAbsoluteModalFooter>
    </ApproveAndCallProgressStepsProvider>
  )
}
